import * as React from "react";

import { Avatar } from "@components/avatar";
import { Button } from "@components/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@components/command";
import { Dialog } from "@components/dialog";
import { BeneficiaryIcon, TeamIcon } from "@components/icons";
import { cn } from "@utils/cn";

import { useAuth0 } from "@auth0/auth0-react";
import { Popover, PopoverContent, PopoverTrigger } from "@components/popover";
import { Skeleton } from "@components/skeleton";
import { PopoverArrow } from "@radix-ui/react-popover";
import { useContact } from "@utils/hooks";
import { TeamGroup } from "@utils/types";
import { useHasValidBeneficiaryOnboarding } from "@pages/onboarding/workflows";

type PopoverTriggerProps = React.ComponentPropsWithoutRef<
  typeof PopoverTrigger
>;

type TeamSwitcherProps = PopoverTriggerProps;

const getMatchingTeam = (teams: TeamGroup[] | undefined, value: string) => {
  const team = (teams ?? [])
    .flatMap((group) => group.teams)
    .find((team) => {
      return team.value === value;
    });

  return team;
};

export function TeamSwitcherLabel() {
  const { contact, team: storedTeam } = useContact();

  // Check if the stored team value matches the contact's ID (Personal) or not (Employer)
  const isPersonal = storedTeam?.value === contact?.id;

  return (
    <div className={cn(
      "text-xs font-semibold text-center rounded-full border-2 px-3 py-1.5",
      {
        "bg-azure-radiance-50 border-azure-radiance-400 text-azure-radiance-700": isPersonal,
        "bg-azure-radiance-100 border-azure-radiance-600 text-azure-radiance-800": !isPersonal,
      }
    )}>
      {isPersonal ? "Beneficiary" : "Employer"}
    </div>
  );
}

export default function TeamSwitcher({ className }: TeamSwitcherProps) {
  const { contact, setStoredTeam, team: storedTeam } = useContact();
  const { logout } = useAuth0();
  const { hasActiveBeneficiaryOnboarding } = useHasValidBeneficiaryOnboarding();

  const [open, setOpen] = React.useState(false);
  const [hoverOpen, setHoverOpen] = React.useState(false);
  const [showNewTeamDialog, setShowNewTeamDialog] = React.useState(false);

  const selectedTeam = React.useMemo(
    () =>
      getMatchingTeam(
        contact?.teams,
        storedTeam?.value ?? (contact?.id as string)
      ),
    [contact?.teams, storedTeam?.value, contact?.id]
  );

  const selectedTeamGroup = React.useMemo(() => {
    if (!contact?.teams || !selectedTeam) return null;

    return contact.teams.find((group) =>
      group.teams.some((team) => team.value === selectedTeam.value)
    );
  }, [contact?.teams, selectedTeam]);

  const isSelectedTeamPersonal = selectedTeamGroup?.label === "Personal";

  // Get the non-selected team (toggle between personal and team)
  const nonSelectedTeam = React.useMemo(() => {
    if (!contact?.teams || !selectedTeam) return null;

    // Filter out Personal team if there's no valid beneficiary onboarding
    const availableTeams = contact.teams
      .filter((group) => {
        if (group.label === "Personal" && !hasActiveBeneficiaryOnboarding) {
          return false;
        }
        return true;
      })
      .flatMap((group) => group.teams);

    const otherTeams = availableTeams.filter(
      (team) => team.value !== selectedTeam.value
    );

    // If current is personal, find a non-personal team, otherwise find personal (if available)
    if (isSelectedTeamPersonal) {
      return otherTeams.find((team) => {
        const teamGroup = contact.teams.find((group) =>
          group.teams.some((t) => t.value === team.value)
        );
        return teamGroup?.label !== "Personal";
      });
    } else {
      // Only return personal if there's valid beneficiary onboarding
      if (hasActiveBeneficiaryOnboarding) {
        return otherTeams.find((team) => {
          const teamGroup = contact.teams.find((group) =>
            group.teams.some((t) => t.value === team.value)
          );
          return teamGroup?.label === "Personal";
        });
      }
      return null;
    }
  }, [contact?.teams, selectedTeam, isSelectedTeamPersonal, hasActiveBeneficiaryOnboarding]);

  const nonSelectedTeamGroup = React.useMemo(() => {
    if (!contact?.teams || !nonSelectedTeam) return null;

    return contact.teams.find((group) =>
      group.teams.some((team) => team.value === nonSelectedTeam.value)
    );
  }, [contact?.teams, nonSelectedTeam]);

  const isNonSelectedTeamPersonal = nonSelectedTeamGroup?.label === "Personal";

  const onClick = React.useCallback(
    (value: string) => {
      setStoredTeam(value);
      setOpen(false);
    },
    [setStoredTeam]
  );

  const TeamGroups = React.useMemo(
    () =>
      contact?.teams
        .filter((group) => {
          // Hide Personal team if there's no valid beneficiary onboarding
          if (group.label === "Personal" && !hasActiveBeneficiaryOnboarding) {
            return false;
          }
          return true;
        })
        .map((group) => {
          return (
            <CommandGroup key={group.label} heading={group.label}>
              {group.teams.map((team) => {
                return (
                  <CommandItem
                    key={team.value}
                    onSelect={() => onClick(team.value)}
                    className="text-sm"
                  >
                    <Avatar
                      className={cn(
                        "mr-2 h-8 w-8 flex items-center justify-center",
                        {
                          "bg-azure-radiance-400": group.label === "Personal",
                          "bg-azure-radiance-600": group.label !== "Personal",
                        }
                      )}
                    >
                      {group.label === "Personal" ? (
                        <BeneficiaryIcon className="h-5 w-5" />
                      ) : (
                        <TeamIcon className="h-5 w-5" />
                      )}
                    </Avatar>
                    {team.label}
                    {selectedTeam?.value === team.value && (
                      <div className="ml-auto bg-success-alert-bg w-2 h-2 rounded-full" />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          );
        }),
    [contact?.teams, selectedTeam?.value, onClick, hasActiveBeneficiaryOnboarding]
  );

  const handleLogout = React.useCallback(() => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [logout]);

  return (
    <Dialog open={showNewTeamDialog} onOpenChange={setShowNewTeamDialog}>
      {/* Hover popover for tooltip */}
      <Popover open={hoverOpen}>
        <PopoverTrigger asChild>
          <div
            className="flex flex-col items-center"
            onMouseEnter={() => !open && setHoverOpen(true)}
            onMouseLeave={() => setHoverOpen(false)}
          >
            {/* Click popover for team switcher */}
            <Popover
              open={open}
              onOpenChange={(open) => {
                setOpen(open);
                setHoverOpen(false);
              }}
            >
              <PopoverTrigger asChild>
                <div className="flex flex-col items-center">
                  {/* Top button: Non-selected team - only show if there are multiple teams */}
                  {nonSelectedTeam && (
                    <Button
                      variant="teamSwitcher"
                      role="combobox"
                      aria-expanded={open}
                      aria-label="Select a team"
                      className={cn("justify-center items-center", className)}
                    >
                      <Avatar
                        className={cn("flex items-center justify-center", {
                          "bg-azure-radiance-400": isNonSelectedTeamPersonal,
                          "bg-azure-radiance-600": !isNonSelectedTeamPersonal,
                        })}
                      >
                        {nonSelectedTeam == null ? (
                          <Skeleton />
                        ) : isNonSelectedTeamPersonal ? (
                          <BeneficiaryIcon className="h-5 w-5" />
                        ) : (
                          <TeamIcon className="h-5 w-5" />
                        )}
                      </Avatar>
                    </Button>
                  )}
                  {/* Bottom button: Selected team */}
                  <Button
                    variant="teamSwitcher"
                    role="combobox"
                    aria-expanded={open}
                    aria-label="Select a team"
                    className={cn(
                      "justify-center items-center",
                      nonSelectedTeam ? "-mt-2" : "",
                      className
                    )}
                  >
                    <Avatar
                      className={cn("flex items-center justify-center", {
                        "bg-azure-radiance-400": isSelectedTeamPersonal,
                        "bg-azure-radiance-600": !isSelectedTeamPersonal,
                      })}
                    >
                      {selectedTeam == null ? (
                        <Skeleton />
                      ) : isSelectedTeamPersonal ? (
                        <BeneficiaryIcon className="h-5 w-5" />
                      ) : (
                        <TeamIcon className="h-5 w-5" />
                      )}
                    </Avatar>
                  </Button>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-72 ml-20 -my-8">
                <Command>
                  <CommandList>{TeamGroups}</CommandList>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={handleLogout}
                      className="text-sm cursor-pointer mt-2"
                    >
                      <svg
                        className="mr-2 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Logout
                    </CommandItem>
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="left"
          className="w-72 p-2 bg-background-highlight"
        >
          <PopoverArrow className="fill-background-highlight" />
          <Command>
            <CommandList>
              <CommandItem className="text-sm aria-selected:bg-transparent">
                <Avatar
                  className={cn(
                    "mr-2 h-8 w-8 flex items-center justify-center",
                    {
                      "bg-azure-radiance-400": isSelectedTeamPersonal,
                      "bg-azure-radiance-600": !isSelectedTeamPersonal,
                    }
                  )}
                >
                  {isSelectedTeamPersonal ? (
                    <BeneficiaryIcon className="h-5 w-5" />
                  ) : (
                    <TeamIcon className="h-5 w-5" />
                  )}
                </Avatar>
                {selectedTeam?.label}
                {selectedTeam?.value === selectedTeam?.value && (
                  <div className="ml-auto bg-success-alert-bg w-2 h-2 rounded-full" />
                )}
              </CommandItem>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Dialog>
  );
}
